import { ArrowRight, Zap } from 'lucide-react';
import type { PublicPaymentPlanStage } from '@/components/public/PaymentPlanPreview';
import { formatStageAmount } from './paymentScheduleAmount';

interface Props {
  stages: PublicPaymentPlanStage[];
}

/**
 * Read-only rendering of a payment plan's stages.
 *
 * Shared so the two surfaces that report a plan they cannot edit — the
 * convert-to-job screen and the estimate builder before its first save — show
 * the customer the same rows in the same order as the PDF.
 */
export function PaymentScheduleStageList({ stages }: Props) {
  if (stages.length === 0) return null;

  return (
    <div className="space-y-2">
      {stages.map((stage) => (
        <div
          key={`${stage.position}-${stage.label}`}
          className="flex flex-col gap-2 rounded-md border bg-background px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-foreground">{stage.label}</p>
              {stage.autoCharge ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                  <Zap className="size-3" />
                  Auto-charge
                </span>
              ) : null}
            </div>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <ArrowRight className="size-3 shrink-0" />
              <span className="truncate">{stage.triggerDescription}</span>
            </p>
          </div>
          <span className="shrink-0 text-sm font-semibold tabular-nums">
            {formatStageAmount(stage.amount)}
          </span>
        </div>
      ))}
    </div>
  );
}
